import type { Lang } from '@/types/lang'
import type { CourseTranslationState } from '@/types/course'
import { resolveLang } from '@/features/editor/components/studio/seedContentLang'
import { hasContentFor } from './langCompleteness'

interface CourseLangFields {
  sourceLang?: Lang
  name?: string | null
  nameEn?: string | null
  description?: string | null
  descriptionEn?: string | null
  publishedLangs?: Lang[]
  translations?: CourseTranslationState[]
}

/**
 * Общий ключ записи видимости языка: тумблер видимости и действия с языком видят одну запись.
 * Иначе пока меняется видимость, язык можно было удалить, и сервер отвечал «языка уже нет».
 */
export const translationPublishKey = (courseId: number): string => `translation-publish-${courseId}`

/** Единственный доступный язык перевода: реестр (ru/en) минус язык оригинала. */
export const translationLangOf = (course: CourseLangFields): Lang =>
  resolveLang(course.sourceLang) === 'ru' ? 'en' : 'ru'

/**
 * Существует ли у курса строка перевода целевого языка — нужна для показа
 * вкладки «Языки» и выбора второго языка (§6.10). Смена языка оригинала
 * разрешена в любой момент, но выбор второго языка доступен только если
 * строка уже существует.
 *
 * Первым спрашивается стартовавший прогон: строку бэк заводит той же
 * транзакцией, что и джобу (§7.1), и до первого переведённого поля её видно
 * только через реестр. Дальше точный ответ — `translations`, и он про ЛЮБУЮ
 * строку, а не только про целевой язык. Остальные два источника — эвристики,
 * которые остаются рабочими, пока поля нет в ответе: `publishedLangs` показывает
 * только ОПУБЛИКОВАННЫЕ языки, непустой второй слот ловит залитый, но не
 * объявленный перевод.
 *
 * Сужать признак до одного точного источника НЕЛЬЗЯ, даже когда он есть:
 * именно широта показывает строку языка с переключателем у курса, которому
 * перевод залили мимо студии. Отними её — и единственным способом объявить
 * язык стал бы платный прогон.
 */
export const hasTranslationRow = (
  course: CourseLangFields,
  hasStartedTranslationRun: boolean,
): boolean => {
  if (hasStartedTranslationRun) return true
  if ((course.translations ?? []).length > 0) return true
  const target = translationLangOf(course)
  const published = course.publishedLangs ?? []
  if (published.includes(target)) return true
  // Тот же предикат заполненности слота, что у шапки редактора и у
  // серверного правила: название ИЛИ описание. Своей копии здесь нет.
  return hasContentFor(target, course)
}

/**
 * Есть ли у курса строка этого языка НА СЕРВЕРЕ — то, что может удалить
 * `DELETE .../translations/{lang}`. Уже, чем `hasTranslationRow`: заполненный
 * слот без строки там считается «языком», но удалять серверу нечего (`404`),
 * и корзина над ним обещала бы действие, которого нет.
 */
export const hasServerTranslationRow = (
  course: CourseLangFields,
  lang: Lang,
  hasStartedTranslationRun: boolean,
): boolean =>
  hasStartedTranslationRun || (course.translations ?? []).some((row) => row.lang === lang)

/**
 * Показан ли язык перевода в каталоге. Читается из СТРОКИ перевода, а не из
 * `publishedLangs`: тот пуст у каждого неопубликованного курса целиком
 * (общекурсовой рубеж), и тумблер по нему показывал «Скрыт» даже объявленному
 * языку черновика — то есть врал на самом массовом для студии классе курсов.
 *
 * `publishedLangs` остаётся фолбэком ровно на один случай: ответ бэка, в
 * котором поля `translations` ещё нет.
 */
export const isTranslationPublished = (course: CourseLangFields, lang: Lang): boolean => {
  const rows = course.translations
  // `== null` ловит и `undefined`, и `null`: серверное правило пишет
  // `translations or []` обеими ветками, а тип поля `null` не обещает — но и
  // соседние поля курса сплошь `string | null`, и бросок отсюда белил бы
  // «Мои курсы» целиком (единственный ErrorBoundary стоит внутри редактора).
  if (rows == null) return (course.publishedLangs ?? []).includes(lang)
  return rows.some((row) => row.lang === lang && row.isPublished)
}

/**
 * Языки ПЕРЕВОДОВ, объявленных показанными. Язык-оригинал сюда не входит: он
 * не перевод и публикуется общим `isPublished` курса.
 *
 * «Объявленных», а не «видимых»: у черновика курса в каталоге нет ни одного
 * языка, но объявленный язык у него есть, и скрывать его — та же ложь, что
 * и подпись тумблера про каталог. Курсы, заведённые до миграции
 * `445ae16ceac8`, все несут опубликованную строку `en` — даже те, где
 * английского текста нет; чип у них честно повторяет то, что сделает
 * каталог после публикации курса.
 *
 * Считается по строкам, а не по `publishedLangs`, по той же причине, что и
 * `isTranslationPublished`: у неопубликованного курса `publishedLangs` пуст
 * целиком, и карточка черновика показывала бы один язык оригинала, хотя
 * второй язык у курса есть и объявлен.
 */
export const publishedTranslationLangs = (course: CourseLangFields): Lang[] => {
  const source = resolveLang(course.sourceLang)
  const rows = course.translations
  const langs =
    rows == null
      ? (course.publishedLangs ?? [])
      : rows.filter((row) => row.isPublished).map((row) => row.lang)
  // Язык оригинала отфильтрован в ОБЕИХ ветках: асимметрия внутри одной
  // функции переживает следующую правку контракта хуже, чем лишняя строка.
  return langs.filter((lang) => lang !== source)
}

/**
 * Язык, содержимое которого уже залито, а строки перевода нет, — то самое
 * состояние, в котором работа автора не видна никому.
 *
 * Зеркало серверного `unpublished_filled_lang`
 * (`api/src/core/services/course_languages.py`), и расходиться с ним нельзя:
 * тем же правилом пользуются подсказка MCP и отчёт по базе, а два разных
 * ответа об одном курсе автор считает поломкой. Отсюда три решения, каждое
 * повторяет серверное:
 *
 * - смотрим на НАЛИЧИЕ строки, а не на публикацию: строка со снятой галочкой
 *   значит, что автор спрятал язык осознанно, и нудеть про это нельзя;
 * - публикацию самого курса НЕ проверяем — у черновиков состояние ровно то
 *   же, а MCP заливает переводы именно в черновики;
 * - граница «заполнено» — название ИЛИ описание во втором слоте.
 *
 * Без поля `translations` (ответ бэка до фазы 4) возвращаем `null`: молчать
 * на неполных данных честнее, чем обвинять автора по догадке.
 *
 * Стартовавший прогон — тот же аргумент и по той же причине, что у
 * `hasTranslationRow`: строку бэк завёл вместе с джобой, а деталь курса до
 * терминального статуса не перечитывается, и без этого признака подсказка
 * висела бы весь прогон перевода — ровно у автора, который уже всё делает
 * правильно.
 */
export const unpublishedFilledLang = (
  course: CourseLangFields,
  hasStartedTranslationRun: boolean,
): Lang | null => {
  if (hasStartedTranslationRun) return null
  const rows = course.translations
  if (rows == null) return null
  const target = translationLangOf(course)
  if (rows.some((row) => row.lang === target)) return null
  return hasContentFor(target, course) ? target : null
}
